import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { MapContainer, TileLayer, Marker } from 'react-leaflet';
import L from 'leaflet';
import { getPublicOrderLiveLocation, updatePublicOrderDestinationLocation } from '../utils/trackingAPI';

const markerIcon = new L.Icon({
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
});

export default function TrackingPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const pollRef = useRef(null);
  const hasAutoUpdatedRef = useRef(false);

  const location = data?.location || null;
  const destination = data?.destination || null;

  const center = useMemo(() => {
    if (destination?.latitude && destination?.longitude) {
      return [destination.latitude, destination.longitude];
    }
    if (location?.latitude && location?.longitude) {
      return [location.latitude, location.longitude];
    }
    return [41.015137, 28.97953];
  }, [destination, location]);

  useEffect(() => {
    let mounted = true;

    async function load() {
      if (!token) {
        setError('Geçersiz takip linki');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const res = await getPublicOrderLiveLocation(token);
        if (!mounted) return;
        setData(res);
      } catch (e) {
        if (!mounted) return;
        setError(e?.message || 'Takip bilgisi alınamadı');
      } finally {
        if (!mounted) return;
        setLoading(false);
      }
    }

    load();

    pollRef.current = setInterval(() => {
      load();
    }, 5000);

    return () => {
      mounted = false;
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [token]);

  const sendMyLocationToBackend = useCallback(async () => {
    if (!token) return;
    if (!navigator.geolocation) return;

    const pos = await new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(resolve, reject, {
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 0,
      });
    });

    await updatePublicOrderDestinationLocation(token, {
      latitude: pos.coords.latitude,
      longitude: pos.coords.longitude,
      source: 'tracking_web',
    });

    // refresh view
    const res = await getPublicOrderLiveLocation(token);
    setData(res);
  }, [token]);

  useEffect(() => {
    // Auto-initialize destination to customer's current location once.
    if (!token) return;
    if (hasAutoUpdatedRef.current) return;
    if (!data) return;

    const hasDest = !!(destination?.latitude && destination?.longitude);
    if (hasDest) {
      hasAutoUpdatedRef.current = true;
      return;
    }

    hasAutoUpdatedRef.current = true;
    sendMyLocationToBackend().catch(() => {
      // ignore
    });
  }, [token, data, destination?.latitude, destination?.longitude, sendMyLocationToBackend]);

  return (
    <div className="min-h-screen bg-gray-50 py-10 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h1 className="font-manrope font-bold text-2xl text-gray-900">Canlı Takip</h1>
              <p className="text-sm text-gray-600 mt-1">Sipariş: {data?.order_code || '-'}</p>
            </div>
            <div className="text-right">
              {loading ? (
                <span className="text-sm text-gray-500">Güncelleniyor…</span>
              ) : (
                <span className="text-sm text-gray-500">Son güncelleme: {location?.created_at || '-'}</span>
              )}
            </div>
          </div>

          {error && (
            <div className="mt-4 rounded-xl bg-red-50 border border-red-100 p-4 text-sm text-red-700">
              {error}
            </div>
          )}

          {!error && !loading && !location && (
            <div className="mt-4 rounded-xl bg-gray-50 border border-gray-100 p-4 text-sm text-gray-700">
              Konum henüz paylaşılmıyor.
            </div>
          )}

          {!error && (
            <div className="mt-4 flex justify-end">
              <button
                onClick={() => {
                  sendMyLocationToBackend().catch(() => {
                    // ignore
                  });
                }}
                className="rounded-xl border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-800 hover:bg-gray-50"
              >
                Konumu Güncelle
              </button>
            </div>
          )}

          <div className="mt-6 overflow-hidden rounded-2xl border border-gray-100">
            <MapContainer center={center} zoom={15} style={{ height: 420, width: '100%' }} scrollWheelZoom>
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              {destination?.latitude && destination?.longitude && (
                <Marker position={[destination.latitude, destination.longitude]} icon={markerIcon} />
              )}
              {location?.latitude && location?.longitude && (
                <Marker position={[location.latitude, location.longitude]} icon={markerIcon} />
              )}
            </MapContainer>
          </div>
        </div>
      </div>
    </div>
  );
}
